import { useUiStrings } from '@/hooks/useUiStrings'

interface DeleteMeetingModalProps {
  /** Dialog heading — varies per action (single/bulk, soft/permanent delete). */
  title: string
  /** Dialog body copy — varies per action (single/bulk, soft/permanent delete). */
  body: string
  /** Confirm-button label; defaults to the generic `t.delete` string. */
  confirmLabel?: string
  deleting: boolean
  onClose: () => void
  onConfirm: () => void
}

/**
 * Generic confirm-delete dialog reused for every delete action in the
 * History page (single/bulk soft-delete on the Active tab, single/bulk
 * permanent delete on the Deleted tab) — copy and confirm label are passed
 * in by the caller so soft-delete and permanent-delete each get distinct,
 * accurate wording.
 */
export default function DeleteMeetingModal({
  title,
  body,
  confirmLabel,
  deleting,
  onClose,
  onConfirm,
}: DeleteMeetingModalProps) {
  const { t } = useUiStrings()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-meeting-title"
    >
      <button
        type="button"
        aria-label={t.closeDeleteMeetingModal}
        className="fixed inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="animate-scale-in relative w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 shadow-2xl sm:p-6">
        <h2 id="delete-meeting-title" className="text-lg font-bold text-[var(--text-primary)]">
          {title}
        </h2>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">{body}</p>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-50"
          >
            {deleting ? t.deleting : (confirmLabel ?? t.delete)}
          </button>
        </div>
      </div>
    </div>
  )
}
